import { useEffect, useId, useState } from 'react';

import { Dialog } from '@shared/ui/dialog';

import { copy } from './copy';
import type { ConfigurationRow } from './types';

/**
 * The edit dialog — one element, two modes, and **it does not write**.
 *
 * Pressing its commit button closes it and opens the confirmation, which is the
 * only thing that touches the row. That is the catalog's own requirement read
 * literally: every configuration write is confirmed before it runs, on the same
 * confirm step the taxonomy, shelf, and plan surfaces already use.
 *
 * **The key is static text, not a disabled field.** A disabled input says «this
 * is editable but not right now», and that is not what is happening: a key is
 * seeded from the service's code and is never editable, here or anywhere.
 *
 * **A secret's value field is removed, not disabled.** Its value is not edited
 * from this dialog under any condition — it is rotated in a dialog of its own —
 * and removing the field with `hidden` takes it out of the accessibility tree
 * and the tab order together, rather than leaving a focusable control that
 * cannot be used.
 *
 * The value field is `dir="ltr"` and in the mono face: a configuration value is
 * a string a service parses, not a quantity a person reads, so it stays Latin
 * however Arabic-Indic every other figure in this console is.
 */
export function EditDialog({
  open,
  row,
  serviceTable,
  seed,
  onReview,
  onCancel,
}: {
  open: boolean;
  row: ConfigurationRow | null;
  /** `<service>__configurations` — which table the operator is about to write. */
  serviceTable: string;
  /**
   * The draft to reopen on, when the service refused the write.
   *
   * The failure toast says «راجعها ثمّ أعد المحاولة», and there is nothing to
   * review if the typed value went with the dialog. Absent — the ordinary open —
   * the fields are seeded from the row.
   */
  seed?: { value?: string; description: string } | null;
  /** Hands the draft to the confirmation. This dialog never writes. */
  onReview: (draft: { value?: string; description: string }) => void;
  onCancel: () => void;
}) {
  const headingId = useId();
  const keyId = useId();
  const valueId = useId();
  const descriptionId = useId();

  const [value, setValue] = useState('');
  const [description, setDescription] = useState('');

  // One dialog serves forty rows, so it is seeded from the row that opened it
  // every time — never left holding the previous key's value.
  useEffect(() => {
    if (!open || !row) return;
    setValue(seed?.value ?? row.value ?? '');
    setDescription(seed?.description ?? row.description);
  }, [open, row, seed]);

  if (!row) return null;

  // The row's stored value decides the field, not the draft: a value that
  // arrived with newlines stays editable as one while the operator collapses it.
  const multiline = (row.value ?? '').includes('\n') || value.includes('\n');

  return (
    <Dialog
      open={open}
      labelledBy={headingId}
      onClose={(returnValue) => {
        if (returnValue !== 'review') onCancel();
      }}
    >
      <form
        method="dialog"
        onSubmit={(event) => {
          event.preventDefault();
          onReview(row.is_secret ? { description } : { value, description });
        }}
        className="flex flex-col gap-4"
      >
        <div>
          <h2 id={headingId} className="text-h3">
            {copy.dialog.title}
          </h2>
          <p className="mb-0 mt-1 max-w-none text-xs text-ink-muted">
            <bdi className="mono">{serviceTable}</bdi>
          </p>
        </div>

        <div className="grid gap-2">
          <span id={keyId} className="text-sm font-medium text-ink">
            {copy.dialog.keyLabel}
          </span>
          <p className="mb-0 max-w-none" aria-labelledby={keyId}>
            <bdi className="mono">{row.key}</bdi>
          </p>
          <p className="mb-0 max-w-none text-xs text-ink-muted">{copy.dialog.keyHint}</p>
        </div>

        {/* Removed for a secret, never disabled. */}
        {row.is_secret ? null : (
          <div className="grid gap-2">
            <label htmlFor={valueId} className="text-sm font-medium text-ink">
              {copy.dialog.valueLabel}
            </label>
            {/*
              A multi-line value gets a multi-line field. `ROBOTS_TXT_BODY` is a
              `robots.txt` body and `<input type="text">` cannot hold a newline:
              opening the dialog and pressing save wrote the three lines back as
              one, producing a file no crawler can parse — and there was no way
              to author a multi-line value from this console at all.

              A single-line value keeps its `<input>`, so `Enter` still reaches
              the commit button for the scalar keys that are most of this table.
            */}
            {multiline ? (
              <textarea
                id={valueId}
                dir="ltr"
                rows={Math.min(12, Math.max(3, value.split('\n').length + 1))}
                value={value}
                onChange={(event) => setValue(event.target.value)}
                className="mono hit-target w-full rounded-sm border border-border-strong bg-surface px-3 py-3 text-sm text-ink"
              />
            ) : (
              <input
                id={valueId}
                type="text"
                dir="ltr"
                value={value}
                onChange={(event) => setValue(event.target.value)}
                className="mono hit-target w-full rounded-sm border border-border-strong bg-surface px-3 text-sm text-ink"
              />
            )}
            <p className="mb-0 max-w-none text-xs text-ink-muted">{copy.dialog.valueHint}</p>
          </div>
        )}

        <div className="grid gap-2">
          <label htmlFor={descriptionId} className="text-sm font-medium text-ink">
            {copy.dialog.descriptionLabel}
          </label>
          <textarea
            id={descriptionId}
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            className="hit-target min-h-[5rem] w-full rounded-sm border border-border-strong bg-surface px-3 py-3 text-sm text-ink"
          />
          <p className="mb-0 max-w-none text-xs text-ink-muted">{copy.dialog.descriptionHint}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {/*
            `type="button"`, not `type="submit"`. The first submit button in a
            form is what `Enter` activates, so a submit-typed cancel meant every
            `Enter` inside the value field silently discarded the edit — no
            request, no confirmation, no message.
          */}
          <button
            type="button"
            formNoValidate
            onClick={onCancel}
            className="rounded-sm border border-border-strong px-5 py-3 text-sm text-ink hover:bg-hover-bg"
          >
            {copy.dialog.cancel}
          </button>
          <button
            type="submit"
            value="review"
            className="rounded-sm bg-accent px-5 py-3 text-sm text-accent-ink hover:bg-accent-hover"
          >
            {copy.dialog.commit}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
